/**
 * Login Page
 */

// Core imports
import React from 'react';
import PropTypes from 'prop-types';

// Style, SEO and settings
import Helmet from 'react-helmet';
import {
  Col,
  css,
  Grid,
  Row,
  up,
  styled,
  th,
  thd,
  withTheme,
} from '@smooth-ui/core-sc';

// Additional Components/Containers
import LoadAsync from '_platform/src/utils/LoadAsync';
import PublicComponent from '_platform/src/utils/PublicComponent';
import getReturnUrlPath from '_platform/src/utils/getReturnUrlPath';

import { withSettings } from 'containers/WebApp/SettingsContext';
import logo from '../../theme/Logo-2026.png';
import cards from './images/login-cards.png';

import RexelBanners from '../../components/CustomComponents/Banners/RexelBanners';

const LoginProvider = LoadAsync(() =>
  import(/* webpackChunkName: "loginProvider" */ '../RexelLogin/LoginProvider')
);
const LoginForm = LoadAsync(() =>
  import(/* webpackChunkName: "loginForm" */ '../RexelLogin/LoginForm')
);

const PageContainer = styled.div`
  align-items: center;
  background-color: ${th('primary')};
  background-image: linear-gradient(45deg, #ac385b 0, #1550a0 100%);
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: center;
  padding-bottom: 60px;
  padding-top: 40px;
  position: relative;

  h4 {
    margin-top: 0;
  }

  .cards {
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 0;
  }
`;

const LoginLogo = styled.h1`
  margin-bottom: 0;
  margin-top: 0;
  padding-bottom: 1rem;
  padding-top: 0;
  color: white;
  display: flex;
  justify-self: center;
  align-items: center;
  flex-direction: column;
  ${up(
    'md',
    css`
      flex-direction: row;
      font-size: 45px;
    `
  )};
  img {
    width: auto;
    padding: 10px;

    ${up(
      'md',
      css`
        max-width: 590px;
      `
    )};
  }
`;

const IntroContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
  color: #fff;
  margin-bottom: 0.5rem;
  margin-top: 0.5rem;
  padding: 1rem 0.5rem;
  border-radius: 20px;
  z-index: 1;
  position: relative;
  ${up(
    'md',
    css`
      padding: 3rem 2rem;
      border-radius: 60px;
    `
  )};
  p {
    color: #fff;
  }
  a {
    color: #fff;
    &:hover {
      text-decoration: none;
    }
    &:visited {
      color: #fff;
    }
  }

  .warning {
    color: #dc3545;
    font-weight: bold;
    text-align: center;
  }

  .link-button-container {
    padding-top: 0;
    text-align: right;

    .forgot-username-button,
    .forgot-password-button,
    .registration-button {
      padding: 0 !important;
      color: #fff;
      text-decoration: underline;
      cursor: pointer;
      &:hover {
        text-decoration: none;
      }
    }
  }
`;

const LoginContainer = styled.div`
  max-width: 500px;
  margin: 0 auto;

  label[for='username'],
  label[for='password'] {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }

  .link-button-container {
    margin-bottom: 1em;
  }

  .reset-password {
    a {
      font-size: 14px;
      font-weight: normal;
    }
  }

  button {
    width: 170px;
    background-color: white;
    border: 1px solid white;
    color: ${thd('primary', '#0154C4')};
    &:hover,
    &:focus {
      color: white;
      background-color: ${thd('primary', '#0154C4')};
    }
  }

  ${up(
    'lg',
    css`
      min-width: 300px;
    `
  )};
`;

const NewPageContainer = styled.div`
  font-size: 16px;

  h2 {
    ${up(
      'lg',
      css`
        margin-top: 0px;
      `
    )};
  }

  h4 {
    margin-bottom: 0;
  }

  h4 + p {
    margin-top: 0;
  }
`;

const LastChanceMessage = styled.div`
  margin: 0 auto;
`;

const LoginPage = ({ settings }) => (
  <PublicComponent
    redirectOnError={
      // Redirect to returnUrl, if it's not the same as loginPagePath
      (getReturnUrlPath !== settings.settingsApp.loginPagePath &&
        getReturnUrlPath) ||
      '/'
    }
  >
    <Helmet>
      <title>Login</title>
      {/*<meta name="description" content="Page description" />*/}
    </Helmet>

    <NewPageContainer>
      <RexelBanners onWhite />
      <PageContainer>
        <img src={cards} className="cards" alt="" />
        <Grid className="page-container">
          <LoginLogo className="text--center">
            <img src={logo} alt="Rexel Online Advantage" />
          </LoginLogo>

          <LastChanceMessage className="spacer--bottom">
            <p className="text--center text--white h1 fancy-font">
              <strong>
                Rewarding your loyalty in 2026 with new ways to earn!
              </strong>
            </p>
          </LastChanceMessage>

          <IntroContainer>
            <Grid>
              <Row>
                <Col xs={12} sm={10} osm={1} lg={6} olg={0}>
                  <h2 className="h3">
                    Rewarding your loyalty in 2026 with new ways to earn!
                  </h2>
                  <p>
                    <b>The Program</b>
                  </p>
                  <p>
                    <b>Promotion Period: 1 February - 30 November 2026</b>
                  </p>
                  <p>
                    <b>Spend $500 + GST Online</b>, on one order with the
                    participating vendor(s) and <b>earn a $50 e-gift card</b>.
                  </p>
                  <p>
                    You can earn gift cards on multiple purchases, as long as
                    each order achieves the spend requirement.{' '}
                    <b>Limited to 4 e-gift cards per month</b>.
                  </p>
                  <p>Terms and conditions apply, log in for more details.</p>

                  <p>
                    For program queries phone:{' '}
                    <a href="tel:1800845034">
                      <b>1800 845 034</b>
                    </a>{' '}
                    or email{' '}
                    <a href="mailto:info@rexelonlineadvantage.com.au">
                      <b>info@rexelonlineadvantage.com.au</b>
                    </a>
                  </p>
                  <p>
                    For log in queries email{' '}
                    <a href="mailto:marketing@rexelha.com.au">
                      <b>marketing@rexelha.com.au</b>
                    </a>
                  </p>
                </Col>
                <Col xs={12} sm={10} osm={1} lg={6} olg={0}>
                  <LoginContainer>
                    <h3 className="h4">Login</h3>
                    <p>Please login below using your webstore details.</p>
                    <LoginProvider disableMeta>
                      <LoginForm
                        formLabels={{
                          labelUserName: 'Username:',
                          labelPassword: 'Password:',
                        }}
                      />
                    </LoginProvider>
                  </LoginContainer>
                </Col>
              </Row>
            </Grid>
          </IntroContainer>
        </Grid>
      </PageContainer>
    </NewPageContainer>
  </PublicComponent>
);

LoginPage.propTypes = {
  settings: PropTypes.object.isRequired,
};

export default withTheme(withSettings(LoginPage));
